import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useShape } from "~/lib/shape-context";

const badgeColors = {
  amber: "#f59e0b",
  blue: "#3b82f6",
  cyan: "#06b6d4",
  emerald: "var(--brand-green-readable)",
  fuchsia: "#d946ef",
  gray: "#a3a3a3",
  green: "var(--brand-green-readable)",
  indigo: "#6366f1",
  lime: "#84cc16",
  orange: "#f97316",
  pink: "#ec4899",
  purple: "#a855f7",
  red: "#ef4444",
  rose: "#f43f5e",
  teal: "#14b8a6",
  violet: "#8b5cf6",
  yellow: "#eab308",
} as const;

type BadgeColor = keyof typeof badgeColors;

const fluidBadgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center overflow-hidden font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,opacity] duration-80 focus-visible:border-ring glider-focus-ring aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/50 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    defaultVariants: {
      size: "md",
      variant: "solid",
    },
    variants: {
      size: {
        lg: "h-7 gap-1.5 px-3 text-meta",
        md: "h-6 gap-1.5 px-2.5 text-caption",
        sm: "h-5 gap-1 px-2 text-micro",
      },
      variant: {
        dot: "border border-border text-foreground",
        solid: "border border-transparent",
      },
    },
  },
);

const legacyBadgeVariants = cva(
  "group/badge inline-flex h-6 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden border border-transparent px-2.5 py-0.5 text-caption font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,opacity] duration-80 focus-visible:border-ring glider-focus-ring has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/50 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        destructive:
          "bg-destructive/10 text-status-negative-foreground dark:bg-destructive/20 [a]:hover:bg-destructive/20",
        ghost:
          "hover:bg-hover hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        outline:
          "border-border text-foreground [a]:hover:bg-hover [a]:hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
      },
    },
  },
);

const badgeVariants = fluidBadgeVariants;

type FluidBadgeVariant = NonNullable<
  VariantProps<typeof fluidBadgeVariants>["variant"]
>;
type LegacyBadgeVariant = NonNullable<
  VariantProps<typeof legacyBadgeVariants>["variant"]
>;
type BadgeSize = NonNullable<VariantProps<typeof fluidBadgeVariants>["size"]>;
type BadgeVariant = FluidBadgeVariant | LegacyBadgeVariant;

interface BadgeProps extends Omit<useRender.ComponentProps<"span">, "color"> {
  color?: BadgeColor;
  disabled?: boolean;
  size?: BadgeSize;
  variant?: BadgeVariant;
}

const FLUID_BADGE_VARIANTS: ReadonlySet<BadgeVariant> = new Set([
  "dot",
  "solid",
]);

function isFluidBadgeVariant(
  variant: BadgeVariant,
): variant is FluidBadgeVariant {
  return FLUID_BADGE_VARIANTS.has(variant);
}

function getDotSize(size: BadgeSize): number {
  switch (size) {
    case "lg":
      return 8;
    case "sm":
      return 6;
    case "md":
      return 7;
  }
}

function getFluidBadgeStyle(params: {
  color: BadgeColor;
  style?: CSSProperties;
  variant: FluidBadgeVariant;
}): CSSProperties {
  if (params.variant !== "solid") {
    return params.style ?? {};
  }

  const colorStyle =
    params.color === "gray"
      ? {
          backgroundColor: "var(--accent)",
          color: "var(--foreground)",
        }
      : {
          backgroundColor: `color-mix(in srgb, ${badgeColors[params.color]} 15%, var(--background))`,
          color: "var(--foreground)",
        };

  return { ...colorStyle, ...params.style };
}

function renderFluidBadgeChildren(params: {
  children: ReactNode;
  color: BadgeColor;
  size: BadgeSize;
  variant: FluidBadgeVariant;
}): ReactNode {
  if (params.variant !== "dot") {
    return params.children;
  }

  const dotSize = getDotSize(params.size);
  const dotColor =
    params.color === "gray" ? "var(--muted-foreground)" : badgeColors[params.color];

  return (
    <>
      <span
        className="shrink-0 rounded-full"
        style={{
          backgroundColor: dotColor,
          height: dotSize,
          width: dotSize,
        }}
      />
      {params.children}
    </>
  );
}

function Badge({
  children,
  className,
  color = "gray",
  render,
  size = "md",
  style,
  variant = "solid",
  ...props
}: BadgeProps) {
  const shape = useShape();

  if (!isFluidBadgeVariant(variant)) {
    return useRender({
      defaultTagName: "span",
      props: mergeProps<"span">(
        {
          children,
          className: cn(legacyBadgeVariants({ variant }), shape.item, className),
          style,
        },
        props,
      ),
      render,
      state: {
        slot: "badge",
        variant,
      },
    });
  }

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        children: renderFluidBadgeChildren({
          children,
          color,
          size,
          variant,
        }),
        className: cn(fluidBadgeVariants({ size, variant }), shape.item, className),
        style: getFluidBadgeStyle({ color, style, variant }),
      },
      props,
    ),
    render,
    state: {
      color,
      size,
      slot: "badge",
      variant,
    },
  });
}

export { Badge, badgeColors, badgeVariants };
export type { BadgeColor, BadgeProps };
